import React, { FC, useMemo } from "react";
import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import type { Matchs } from "../../../types";

const BUCKET_MINUTES = 15;

export type HeatmapBlock = {
  match: Matchs;
  start: Date;
  end: Date;
};

type TablesOccupancyHeatmapProps = {
  blocks: HeatmapBlock[];
  tableCount: number;
  rangeStartMs: number;
  rangeEndMs: number;
};

type Bucket = {
  startMs: number;
  endMs: number;
  occupiedTables: number;
  ratio: number;
};

function buildBuckets(
  blocks: HeatmapBlock[],
  tableCount: number,
  rangeStartMs: number,
  rangeEndMs: number
): Bucket[] {
  if (rangeEndMs <= rangeStartMs || tableCount <= 0) return [];

  const bucketMs = BUCKET_MINUTES * 60_000;
  const buckets: Bucket[] = [];

  for (let t = rangeStartMs; t < rangeEndMs; t += bucketMs) {
    const end = Math.min(t + bucketMs, rangeEndMs);
    const occupied = new Set<number>();
    for (const block of blocks) {
      if (block.match.tableId == null) continue;
      if (
        block.start.getTime() < end &&
        block.end.getTime() > t
      ) {
        occupied.add(block.match.tableId);
      }
    }
    const occupiedTables = occupied.size;
    buckets.push({
      startMs: t,
      endMs: end,
      occupiedTables,
      ratio: occupiedTables / tableCount,
    });
  }
  return buckets;
}

function formatHourMinute(ms: number): string {
  return new Date(ms).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function heatColor(ratio: number, isDark: boolean): string {
  if (ratio <= 0) return isDark ? alpha("#fff", 0.06) : alpha("#0B1F33", 0.06);
  // 0 → bleu froid, 1 → orange saturé
  const t = Math.min(1, Math.max(0, ratio));
  const r = Math.round(60 + t * 180);
  const g = Math.round(140 - t * 80);
  const b = Math.round(220 - t * 160);
  return `rgb(${r}, ${g}, ${b})`;
}

const TablesOccupancyHeatmap: FC<TablesOccupancyHeatmapProps> = ({
  blocks,
  tableCount,
  rangeStartMs,
  rangeEndMs,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const buckets = useMemo(
    () => buildBuckets(blocks, tableCount, rangeStartMs, rangeEndMs),
    [blocks, tableCount, rangeStartMs, rangeEndMs]
  );

  const peak = useMemo(() => {
    if (buckets.length === 0) return null;
    return buckets.reduce((best, b) => (b.ratio > best.ratio ? b : best));
  }, [buckets]);

  const hourLabels = useMemo(() => {
    const labels: { ms: number; label: string }[] = [];
    const start = new Date(rangeStartMs);
    start.setMinutes(0, 0, 0);
    if (start.getTime() < rangeStartMs) start.setHours(start.getHours() + 1);
    for (let t = start.getTime(); t <= rangeEndMs; t += 60 * 60_000) {
      labels.push({
        ms: t,
        label: `${new Date(t).getHours()}h`,
      });
    }
    return labels;
  }, [rangeStartMs, rangeEndMs]);

  if (tableCount === 0) {
    return (
      <Typography color="text.secondary">Aucune table configurée.</Typography>
    );
  }

  if (buckets.length === 0) {
    return (
      <Typography color="text.secondary">
        Aucune donnée d&apos;occupation pour cette journée.
      </Typography>
    );
  }

  const rangeDuration = Math.max(rangeEndMs - rangeStartMs, 1);

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: 1,
        borderColor: "divider",
        bgcolor: isDark ? "#1A1F28" : "#fff",
        p: 2.5,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Saturation des tables
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Part des tables occupées par tranche de {BUCKET_MINUTES} min
          </Typography>
        </Box>
        {peak && (
          <Typography variant="body2" color="text.secondary">
            Pic : {Math.round(peak.ratio * 100)}% (
            {peak.occupiedTables}/{tableCount}) à{" "}
            {formatHourMinute(peak.startMs)}
          </Typography>
        )}
      </Stack>

      <Box sx={{ position: "relative", height: 28, mb: 0.5 }}>
        {hourLabels.map(({ ms, label }) => {
          const left = ((ms - rangeStartMs) / rangeDuration) * 100;
          return (
            <Typography
              key={ms}
              variant="caption"
              sx={{
                position: "absolute",
                left: `${left}%`,
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {label}
            </Typography>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          height: 56,
          borderRadius: 1.5,
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
        }}
      >
        {buckets.map((bucket) => (
          <Box
            key={bucket.startMs}
            title={`${formatHourMinute(bucket.startMs)}–${formatHourMinute(bucket.endMs)} : ${bucket.occupiedTables}/${tableCount} tables (${Math.round(bucket.ratio * 100)}%)`}
            sx={{
              flex: bucket.endMs - bucket.startMs,
              bgcolor: heatColor(bucket.ratio, isDark),
              borderRight: `1px solid ${alpha("#000", isDark ? 0.25 : 0.08)}`,
              minWidth: 2,
              transition: "filter 0.12s ease",
              "&:hover": { filter: "brightness(1.12)" },
            }}
          />
        ))}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mt: 1.5 }}
      >
        <Typography variant="caption" color="text.secondary">
          Libre
        </Typography>
        <Box sx={{ display: "flex", height: 10, borderRadius: 999, overflow: "hidden", width: 120 }}>
          {[0, 0.25, 0.5, 0.75, 1].map((r) => (
            <Box key={r} sx={{ flex: 1, bgcolor: heatColor(r, isDark) }} />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Saturé
        </Typography>
      </Stack>
    </Box>
  );
};

export default TablesOccupancyHeatmap;
